import type { Node, Edge } from '@xyflow/react';
import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import type React from 'react';
import type { ValidationError } from './fieldValidation';
import type { FilterControlFlowField } from '@/apis/logiblocksAPI';
import type { RunStep } from './runHistory/runStepUtils';

/**
 * LBNodeData with an index signature, allowing access to unknown keys.
 */
export type LBNodeDataWithIndexer = LBNodeData & {
  [key: string]: unknown;
};

/** React Flow node carrying our LBNodeData payload. */
export type ReactFlowNode = Node<LBNodeDataWithIndexer>;

/**
 * Payload carried on an LBEdge.
 *
 * Unlike LBNode, the proto LBEdge is flat — it has no nested data message —
 * so we declare this type ourselves for the React Flow runtime, where
 * edgeOrder is extracted from LBEdge and stored on edge.data.
 */
export interface LBEdgeData {
  // Optional because SINGLE-output edges constructed in insertNewChildRaw
  // carry no data field at all; when data IS present, edgeOrder is always set.
  edgeOrder?: number;
}

/**
 * LBEdgeData with an index signature, required by React Flow's Edge<T>
 * constraint. Mirrors LBNodeDataWithIndexer.
 */
export type LBEdgeDataWithIndexer = LBEdgeData & {
  [key: string]: unknown;
};

/** React Flow edge carrying our LBEdgeData payload. */
export type ReactFlowEdge = Edge<LBEdgeDataWithIndexer>;

export enum OutputType {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
  // Node is the root of a subgraph. LBNodeInfo must also implement getSubgraphIndex().
  SUBGRAPH_PARENT = 'SUBGRAPH_PARENT',
}

export enum NodeCategory {
  TRIGGER = 'TRIGGER',
  ACTION = 'ACTION',
  CONTROL_FLOW = 'CONTROL_FLOW',
  NOTIFICATIONS = 'NOTIFICATIONS',
}

/** Human-readable labels for node categories */
const CATEGORY_LABELS: Record<NodeCategory, string> = {
  [NodeCategory.TRIGGER]: 'Triggers',
  [NodeCategory.ACTION]: 'Actions',
  [NodeCategory.CONTROL_FLOW]: 'Flow Control',
  [NodeCategory.NOTIFICATIONS]: 'Notifications',
};

export enum CATEGORY_ICON_BG {
  TRIGGER = 'var(--info-thin)',
  ACTION = 'var(--danger-thin)',
  CONTROL_FLOW = 'var(--light-warning)',
  NOTIFICATIONS = 'var(--category-cyan-thin)',
}

/** Get the human-readable label for a node category */
export function getCategoryLabel(category: NodeCategory): string {
  return CATEGORY_LABELS[category];
}

/**
 * A full-panel screen that a node's customization opens over its main form, like the Alert
 * trigger's "Add alert type". While one is open the panel shows its title with a back arrow in
 * place of the node header, and its footer in place of Save / Cancel. The customization component
 * renders the screen's body itself and closes the screen by passing `null` to `setPanelScreen`.
 */
export interface PanelScreen {
  title: string;
  /** The back arrow, and the footer's Cancel. */
  onBack: () => void;
  /** Text beside the back arrow, for example "Delay". Leave unset for the arrow alone. */
  backLabel?: string;
  /** The footer's main button. Leave unset for a screen whose footer is only Cancel. */
  primaryLabel?: string;
  onPrimary?: () => void;
}

export interface CustomizationComponentProps {
  /** The ID of node being edited */
  nodeId: string;
  /**
   * The node's protobuf data.
   *
   * Readonly: customization components commit edits via `onChange`, which
   * flows through `updateLBNodeData` / `replaceLBNodeData` in the parent.
   */
  nodeData: Readonly<LBNodeData>;
  /**
   * Callback invoked when user modifies any field. Receives a partial
   * LBNodeData patch; the parent merges it onto the existing node data so
   * customizations don't have to round-trip unrelated fields (e.g.
   * custom_display_name, user_variables).
   */
  onChange: (updatedData: Partial<LBNodeData>) => void;
  /** When true, all inputs are disabled and the user cannot modify the configuration. */
  isReadOnly?: boolean;
  isSidePannelOpen?: boolean;
  step?: number;
  setStep?: React.Dispatch<React.SetStateAction<number>>;
  /** Opens (or, with null, closes) a full-panel screen. See PanelScreen. */
  setPanelScreen?: (screen: PanelScreen | null) => void;
  onAddSelectedTypes?: () => void;
  childAddSelectedTypesRef?: React.MutableRefObject<(() => void) | null>;
  /** ID of the parent workflow – used to pre-fetch dropdown labels on panel open. */
  workflowId?: string;
  /**
   * Validation errors for the current draft of this node. Components are
   * responsible for surfacing these beneath the relevant fields.
   */
  validationErrors?: ValidationError[];
  /**
   * Fields pre-fetched for filterControlFlow nodes only. Passed directly from
   * LBCanvas so the data is never stored in node state. Conditional nodes do
   * not use this prop — they fetch lazily inside ConditionalCustomization.
   */
  prefetchedFields?: FilterControlFlowField[];

  /**
   * ID of the parent node in the graph, resolved by LBCanvas when the panel
   * opens. Used by filterControlFlow to make paginated field API calls without
   * needing to store parent metadata on the node.
   */
  prefetchedFieldsParentId?: string;
}

/**
 * Type for customization components.
 *
 * New components should not use the `showDeprecatedValidationErrorBanner`
 * flag, and should instead show their own per-field error UI. (This flag
 * just exists while we're refactoring existing components away from
 * the old error UI.)
 */
export type CustomizationComponent = React.FC<CustomizationComponentProps> & {
  /**
   * @deprecated - remove this flag when all nodes have been updated to
   * use inline errors
   */
  showDeprecatedValidationErrorBanner?: boolean;
};

/** Props for a node type's run step detail view. */
export interface RunStepDetailProps {
  step: RunStep;
  /** The nodes of the graph the run executed, which label the variables the step read. */
  runGraphNodes: ReactFlowNode[];
  /** The user's `date_format` preference, which dates in the detail follow. */
  dateFormat: string | undefined;
}

export interface GraphData {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
}

/**
 * NodeType-specific data and logic.
 * Each node type defines its own LBNodeInfo with a unique dataKey.
 */
export interface LBNodeInfo {
  /** The protobuf data field key this node uses (e.g., 'alert_trigger').
   * Also serves as the node type identifier. */
  protobufNodeDataKey: keyof LBNodeData;

  /** The category this node belongs to (trigger, action, or control flow). */
  category: NodeCategory;

  /** Human-readable display name for this node type */
  label: string;

  /** Whether this node accepts incoming connections */
  hasInput: boolean;

  /** Returns the icon component for this node type */
  getIcon: (size: number) => React.ReactNode;

  /** Defines how many outgoing connections this node supports */
  outputType: OutputType;

  /**
   * Returns a subtitle string based on the node's configuration.
   *
   * Undefined if the node doesn't display a subtitle.
   *
   * @param nodeData - The protobuf data for this node.
   * @param graph - The entire ReactFlow graph.
   */
  getSubtitle?: (nodeData: LBNodeData, graph: GraphData) => React.ReactNode;

  /**
   * Return a list of validation errors that must be fixed on this node before
   * the graph can be published. These will generally be variable-related:
   * referencing a variable that doesn't exist, or comparing a variable to
   * a value of the wrong type.
   *
   * Undefined if the node cannot be misconfigured.
   *
   * @param nodeId - id of the node being validated
   * @param nodeData - protobuf data for the node being validated
   * @param graph - The entire ReactFlow graph.
   */
  getValidationErrors?: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => ValidationError[] | undefined;

  /**
   * Returns a one-line description of what this node did in a run, from its step's report, or
   * null if the step has no report to describe. Dates follow `dateFormat`, the user's
   * `date_format` preference.
   *
   * Undefined if the node type's steps have no summary.
   */
  getRunStepSummary?: (
    step: RunStep,
    dateFormat: string | undefined
  ) => string | null;

  /**
   * Component showing what this node did in a run, from its step's report, when the step is
   * expanded.
   *
   * Undefined if the node type's steps have no detail, so they don't expand.
   */
  RunStepDetailComponent?: React.FC<RunStepDetailProps>;

  /** Component for customizing this node's configuration in the side panel */
  CustomizationComponent?: CustomizationComponent;

  /**
   * True if this node type requires a SHIPMENT-typed variable to be
   * available from its ancestors to be configurable. When set, the node
   * picker disables adding this node type where no shipment is upstream,
   * instead of letting the user configure an uncompletable node.
   */
  requiresShipmentInContext?: boolean;

  /**
   * True if nothing can be added after this node: it ends its branch, so the canvas shows no
   * "+" below it.
   */
  endsBranch?: boolean;

  /**
   * Why this node type cannot be added below `parentNodeId` in `graph`, or undefined when it
   * can. The node picker shows the entry disabled with this reason.
   */
  getPickerUnavailableReason?: (
    parentNodeId: string,
    graph: GraphData
  ) => string | undefined;

  /** Creates default node data for this node type */
  createDefaultNodeData: () => LBNodeDataWithIndexer;

  /**
   * SUBGRAPH_PARENT nodes must implement this.
   * Returns the subgraph index from node data.
   * Returns undefined if no subgraph is attached yet.
   */
  getSubgraphIndex?: (nodeData: LBNodeData) => number | undefined;

  /**
   * SUBGRAPH_PARENT nodes must implement this.
   * Sets the subgraph index on the given node data.
   */
  setSubgraphIndex?: (nodeData: LBNodeData, index: number) => void;

  /**
   * If true, open the customize-node panel for this node immediately after
   * creating it. Set for nodes that require customization in order to
   * be valid.
   */
  showCustomizePanelOnCreation?: boolean;
}
